import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { PayeeCasesService } from '../payee-cases.service';
import { environment } from 'src/environments/environment';
import axios, { AxiosError } from 'axios';
import { DebtorOption } from '../types';
import { ChipDropdownComponent } from '../chip-dropdown/chip-dropdown.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import {
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'payee-case-debtor-name-filter',
  templateUrl: './case-debtor-name-filter.component.html',
  styleUrls: ['./case-debtor-name-filter.component.scss'],
  imports: [
    FormsModule,
    ChipDropdownComponent,
    PayeeFormFieldComponent,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatProgressSpinner,
    MatAutocomplete,
    MatOption,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class CaseDebtorNameFilterComponent implements OnInit, OnDestroy {
  private payeeCasesService = inject(PayeeCasesService);

  private readonly destroy = new Subject<void>();
  private readonly debtorSearch$ = new Subject<string>();
  readonly debtorNameControl = new FormControl<string | null>('', [
    Validators.required,
    Validators.minLength(3),
  ]);

  debtorOptions: DebtorOption[] = [];
  readonly loading = signal(false);

  dropdownOpen = false;

  get selected() {
    return this.payeeCasesService.debtor_name.value;
  }

  ngOnInit(): void {
    this.payeeCasesService.debtor_name
      .pipe(takeUntil(this.destroy))
      .subscribe((v) => this.debtorNameControl.patchValue(v, { emitEvent: false }));

    this.debtorSearch$
      .pipe(takeUntil(this.destroy), debounceTime(1000), distinctUntilChanged())
      .subscribe(async (name) => {
        if (name) {
          await this.setDebtors(name);
        }
      });

    this.payeeCasesService.client
      .pipe(takeUntil(this.destroy))
      .subscribe(() => (this.debtorOptions = []));
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.debtorSearch$.complete();
  }

  onDebtorSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.debtorSearch$.next(value);
  }

  onPaste(_event: ClipboardEvent): void {
    this.debtorNameControl.markAsTouched();
    setTimeout(() => {
      this.applyFilter();
    });
  }

  private async setDebtors(name?: string): Promise<void> {
    if (!this.payeeCasesService.getDebtorsPath.value) {
      console.warn('Get debtors path is not set!');
      return;
    }

    if (!this.dropdownOpen || !name || name.length < 3) {
      return;
    }

    try {
      this.loading.set(true);

      const response = await axios.get<{ debtors: DebtorOption[] }>(
        `${environment.baseUrl}/${this.payeeCasesService.getDebtorsPath.value}`,
        {
          params: {
            name,
            client: this.payeeCasesService.client.value?.uuid,
          },
        },
      );

      this.debtorOptions = response.data.debtors;
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error(
          'Network error while searching debtors:',
          error.response?.data,
        );
      } else {
        console.error('Unknown error searching debtors:', error);
      }
    } finally {
      this.loading.set(false);
    }
  }

  applyFilter(): void {
    if (this.debtorNameControl.invalid) {
      this.debtorNameControl.markAsDirty();
      return;
    }

    this.payeeCasesService.debtor_name.next(this.debtorNameControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.payeeCasesService.debtor_name.next(null);
    this.dropdownOpen = false;
  }

  displayWith(selected: string): string {
    return selected;
  }
}
